<template>
  <div class="login-wrap">
    <div class="login-card">
      <h1 class="login-title">口令验证</h1>
      <p class="login-desc">请输入授权口令进入后台</p>
      <NInput
        v-model:value="token"
        type="password"
        show-password-on="click"
        placeholder="输入口令..."
        size="large"
        class="login-input"
        @keyup.enter="doLogin"
      />
      <NButton type="primary" size="large" block :loading="loading" @click="doLogin" class="login-btn">
        验证
      </NButton>
      <p class="login-err" v-if="errMsg">{{ errMsg }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { NInput, NButton } from 'naive-ui'

const router = useRouter()
const route = useRoute()
const token = ref('')
const loading = ref(false)
const errMsg = ref('')

function saveToken(t: string) {
  localStorage.setItem('auth_token', t)
  const to = (route.query.redirect as string) || '/'
  router.replace(to)
}

onMounted(() => {
  const urlToken = route.query.token as string
  if (urlToken) {
    saveToken(urlToken)
    return
  }
  const saved = localStorage.getItem('auth_token')
  if (saved) {
    saveToken(saved)
  }
})

async function doLogin() {
  const t = token.value.trim()
  if (!t) {
    errMsg.value = '请输入口令'
    return
  }
  loading.value = true
  errMsg.value = ''
  // 用后端任意需要认证的接口验证 token
  try {
    const resp = await fetch(`${globalThis.location.origin}/api/game/user-list?page=1&pageSize=1`, {
      headers: { 'X-Auth-Token': t, 'Content-Type': 'application/json' }
    })
    if (resp.status === 200) {
      saveToken(t)
    } else if (resp.status === 401) {
      errMsg.value = '口令错误，请重试'
    } else {
      saveToken(t) // 其他错误也先存着
    }
  } catch {
    saveToken(t) // 网络不通也先存着
  } finally {
    loading.value = false
  }
}
</script>

<style scoped lang="scss">
.login-wrap {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-image:
    radial-gradient(70% 52% at 50% 14%, rgba(216, 178, 95, .16), transparent 70%),
    radial-gradient(95% 85% at 50% 62%, rgba(0, 0, 0, .50), transparent 76%);
}

.login-card {
  position: relative;
  width: 100%;
  max-width: 380px;
  padding: 38px 30px 30px;
  border-radius: 18px;
  text-align: center;
  background-image: var(--kp-card-bg);
  border: 1px solid var(--kp-gold-line-strong);
  box-shadow: var(--kp-card-shadow);
  animation: card-in .42s cubic-bezier(.2, .7, .2, 1) both;

  // 顶部一道金线
  &::before {
    content: '';
    position: absolute;
    left: 14px;
    right: 14px;
    top: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(246,226,178,.62) 22%, rgba(255,246,224,.92) 50%, rgba(246,226,178,.62) 78%, transparent);
    animation: kp-edge-glow 2.6s ease-in-out infinite;
    pointer-events: none;
  }
}

.login-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 6px;
  text-indent: 6px;
  margin-bottom: 10px;
  background-image: linear-gradient(180deg, #fdf3da, #d9b773 58%, #b3893f);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 1px 0 rgba(0, 0, 0, .6));
}

.login-desc {
  font-size: 12px;
  color: var(--kp-muted);
  letter-spacing: 1.2px;
  margin-bottom: 26px;
}

.login-input {
  margin-bottom: 16px;
}

.login-btn {
  margin-bottom: 12px;
  letter-spacing: 2px;
  font-weight: 700;
}

.login-err {
  color: var(--kp-bad-soft);
  font-size: 12px;
  letter-spacing: .4px;
  margin-top: 6px;
}

@keyframes card-in {
  from { opacity: 0; transform: translateY(8px) scale(.985); }
  to   { opacity: 1; transform: none; }
}
</style>
